<template>
  <div id="upload-withholding-document-page">
    <v-container>
      <v-alert
        v-model="alert"
        type="success"
        color="primary"
        transition="slide-y-transition"
        dismissible
      >
        คุณได้ทำการอัปโหลดเอกสารหัก ณ ที่จ่ายสำเร็จแล้ว
      </v-alert>

      <v-card class="pa-10 mb-8">
        <div class="_fs-36">
          แนบเอกสารภาษีหัก ณ ที่จ่าย
        </div>
        <div>แนบรูปหลักฐานเอกสารภาษีหักของท่าน</div>

        <v-divider class="mt-4 mb-8" />

        <validation-observer ref="uploadWithholdingDocumentForm">
          <!-- Top Section - Order Information (Readonly) -->
          <div class="mb-8">
            <base-text-field
              label="เลขที่เอกสาร"
              :value="order.code"
              readonly
            />

            <base-text-field
              label="ราคาสุทธิสินค้าที่เสียภาษี (บาท) / Pre-VAT Amount"
              :value="formatNumber(order.preVatAmount)"
              readonly
            />

            <base-text-field
              label="ภาษีถูกหัก ณ ที่จ่าย (บาท) / Withholding tax"
              :value="formatNumber(order.withholdingTax)"
              readonly
            />
          </div>

          <v-divider class="my-8" />

          <!-- Payment Method Section (Readonly) -->
          <div class="_fs-24 mb-4">
            วิธีการชำระเงิน
          </div>

          <v-radio-group
            :value="paymentMethod"
            row
            readonly
            disabled
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="qrCode"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-qrcode</v-icon>
                      QR Code
                    </div>
                  </template>
                </v-radio>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="transfer"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-cash-multiple</v-icon>
                      โอน/ชำระผ่านบัญชีธนาคาร
                    </div>
                  </template>
                </v-radio>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="byCreditCard"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-credit-card-outline</v-icon>
                      บัตรเครดิต/บัตรเดบิต
                    </div>
                  </template>
                </v-radio>
              </v-col>
            </v-row>
          </v-radio-group>

          <v-divider class="my-8" />

          <!-- File Upload Display -->
          <template v-if="withholdingDocumentSrc">
            <v-row
              class="mb-4"
              justify="center">
              <v-col
                v-for="(item, index) in listWithholdingDocumentSrc"
                :key="index"
                cols="auto"
                class="ml-2"
                style="position: relative;"
              >
                <img
                  v-if="isImageDocument(item.documentFile)"
                  :src="item.src"
                  alt="Upload file"
                  width="100%"
                  height="200px"
                >
                <div
                  v-else
                  style="display: flex; align-items: center; max-width: 200px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
                  <v-icon
                    large
                    color="red">
                    mdi-file-pdf-box
                  </v-icon>
                  <span class="ml-2">{{ item.documentFile.name }}</span>
                </div>
                <v-icon
                  color="black"
                  style="position: absolute; top: 15px; right: 15px;"
                  size="24"
                  @click="removeWithholdingDocumentFile(index)"
                >
                  mdi-close-circle
                </v-icon>
              </v-col>
            </v-row>
          </template>

          <!-- File Upload Input -->
          <validation-provider
            v-slot="{ errors }"
            ref="withholdingDocument"
            rules="fileUpload|maximumSizeFile:10"
            name="เอกสารหัก ณ ที่จ่าย">
            <v-file-input
              ref="withholdingDocumentFile"
              label="เอกสารหัก ณ ที่จ่าย"
              filled
              rounded
              multiple
              accept="image/jpg, image/jpeg, image/png, application/pdf"
              prepend-icon="mdi-file-document-outline"
              :messages="`กรุณาอัพโหลดเอกสารหัก ณ ที่จ่าย ที่มีนามสกุลไฟล์เป็น jpg, png, pdf และมีขนาดไฟล์ไม่เกิน 10mb`"
              :error-messages="errors"
              :value="withholdingDocumentFile"
              :clearable="false"
              @change="uploadWithholdingDocumentFile"
            />
          </validation-provider>

          <v-row
            class="mt-8"
            justify="center">
            <v-btn
              color="primary"
              rounded
              min-width="150"
              :loading="isSubmitting"
              :disabled="!withholdingDocumentSrc"
              @click="submit">
              ยืนยัน
            </v-btn>
          </v-row>
        </validation-observer>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import store from '@/store'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'UploadWithholdingDocument',
  components: {
    ValidationObserver,
    ValidationProvider,
    BaseTextField: () => import('@/components/BaseTextField')
  },
  props: {
    orderId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      alert: false,
      isSubmitting: false,
      withholdingDocumentSrc: null,
      listWithholdingDocumentSrc: [],
      withholdingDocumentFile: null
    }
  },
  computed: {
    ...mapState('Order', ['order']),
    paymentMethod() {
      if (!this.order || !this.order.payments || this.order.payments.length === 0) {
        return null
      }

      // Find the last payment with type 'slip', 'creditCard', or 'qr'
      const validTypes = ['slip', 'creditCard', 'qr']
      const lastPayment = [...this.order.payments]
        .reverse()
        .find((payment) => validTypes.includes(payment.type))

      if (!lastPayment) {
        return null
      }

      // Map API payment types to component values
      const typeMapping = {
        slip: 'transfer',
        creditCard: 'byCreditCard',
        qr: 'qrCode'
      }

      return typeMapping[lastPayment.type] || null
    }
  },
  async beforeRouteEnter(to, from, next) {
    try {
      store.dispatch('wait/start', 'app loading', { root: true })

      const order = await store.dispatch('Order/fetchOrderById', to.params.orderId)
      store.dispatch('wait/end', 'app loading', { root: true })
      if (order.status === 'deleted') {
        next({ name: 'NotFound' })
      } else {
        next()
      }
    } catch {
      next({ name: 'NotFound' })
    }
  },
  methods: {
    formatNumber(value) {
      if (!value) return '0.00'
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    isImageDocument(file) {
      return file && file.type && file.type.startsWith('image/')
    },
    uploadWithholdingDocumentFile(files) {
      if (!files || files.length === 0) {
        this.withholdingDocumentSrc = null
        this.listWithholdingDocumentSrc = []
        this.withholdingDocumentFile = null
        return
      }

      this.withholdingDocumentFile = files
      this.listWithholdingDocumentSrc = []

      files.forEach((file) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const documentData = {
            src: e.target.result,
            documentFile: file
          }
          this.listWithholdingDocumentSrc.push(documentData)
          this.withholdingDocumentSrc = e.target.result
        }
        reader.readAsDataURL(file)
      })
    },
    removeWithholdingDocumentFile(index) {
      this.listWithholdingDocumentSrc.splice(index, 1)
      if (this.listWithholdingDocumentSrc.length === 0) {
        this.withholdingDocumentSrc = null
        this.$refs.withholdingDocumentFile.reset()
      }
    },
    async submit() {
      const canSubmit = await this.$refs.uploadWithholdingDocumentForm.validate()
      if (!canSubmit) {
        this.$utils.scrollToErrorField('uploadWithholdingDocumentForm', this)
        return
      }

      this.isSubmitting = true

      try {
        const bodyData = {
          type: 'withholding-document',
          totalImage: this.withholdingDocumentFile.length
        }

        const formData = new FormData()
        if (this.withholdingDocumentFile && this.withholdingDocumentFile.length > 0) {
          this.withholdingDocumentFile.forEach((file) => {
            formData.append('file', file)
          })

          formData.append('bodyData', JSON.stringify(bodyData))

          // const response = await this.updateOrderPayments({ id: this.order._id, params: formData })

          this.alert = true

          // Show success modal
          this.showSuccessModal()
        }
      } catch (error) {
        // console.error('Upload withholding document error:', error)
        // Handle error - show error message
      } finally {
        this.isSubmitting = false
      }
    },
    showSuccessModal() {
      this.$swal({
        icon: 'success',
        title: 'แบบเอกสารเรียบร้อย',
        text: 'สามารถดาวน์โหลดใบเสร็จรับเงิน/ใบกำกับภาษี ได้ที่หน้ารายการสั่งซื้อ ภายใน 1-3 วันทำการ',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#4CAF50',
        allowOutsideClick: false
      })
    },
    ...mapActions('Order', ['updateOrderPayments'])
  }
}
</script>

<style lang="scss" scoped>
._fs-36 {
  font-size: 36px;
  font-weight: bold;
  line-height: 1.2;
}

._fs-24 {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
}

._base-withholding-upload-card {
  .v-card {
    border: 2px dashed #e0e0e0;
    background-color: #fafafa;

    &:hover {
      border-color: #4caf50;
      background-color: #f1f8e9;
    }
  }
}

.HintTextColor {
  ::v-deep .v-messages__message {
    color: #757575 !important;
  }
}

</style>
